import React, {useCallback, useState} from 'react';
import ReactDiffViewer from 'react-diff-viewer';

import {RunWithRunsetInfo} from '../containers/RunsDataLoader';
import {useLoadFile, useLoadFileInArtifact} from '../util/requests';
import {InstrumentedLoader as Loader} from './../components/utility/InstrumentedLoader';

interface CodeDiffProps {
  runA?: RunWithRunsetInfo;
  runB?: RunWithRunsetInfo;
  entityName: string;
  projectName: string;
  split: boolean;
  setError: (err: string | undefined) => void;
}

const CodeDiff: React.FC<CodeDiffProps> = props => {
  const {runA, runB, entityName, projectName, split} = props;
  const [codeA, setCodeA] = useState('');
  const [codeB, setCodeB] = useState('');

  const isUsingCodeSavingA = codePath(runA!._wandb) !== '';
  const isUsingCodeSavingB = codePath(runB!._wandb) !== '';
  const codePathA = isUsingCodeSavingA
    ? codePath(runA!._wandb)
    : (runA!.runInfo?.codePath ?? '');
  const codePathB = isUsingCodeSavingB
    ? codePath(runB!._wandb)
    : (runB!.runInfo?.codePath ?? '');

  function normalize(code: string, path: string) {
    // This extracts the source from notebooks for sane diffs
    if (path.endsWith('ipynb')) {
      try {
        const parsed = JSON.parse(code);
        let normalized = '';
        parsed.cells.forEach((cell: any) => {
          normalized += cell.source.join('') + '\n';
        });
        return normalized;
      } catch (e) {
        console.error('Failed to parse code: ', e);
        return code;
      }
    } else {
      return code;
    }
  }
  const setCodeACallback = useCallback(
    (code: string) => setCodeA(normalize(code, codePathA)),
    [setCodeA, codePathA]
  );
  const setCodeBCallback = useCallback(
    (code: string) => setCodeB(normalize(code, codePathB)),
    [setCodeB, codePathB]
  );
  const setErrorCallback = useCallback(
    () => props.setError('Problem loading code'),
    [props]
  );

  function codePath(wandb: any) {
    return (wandb.code_path || wandb.session_history) ?? '';
  }

  const loadingFileA = useLoadFile(
    {entityName, projectName, runName: runA!.name},
    codePathA,
    {
      onSuccess: setCodeACallback,
      onFailure: setErrorCallback,
      fallback: setErrorCallback,
      skip: !isUsingCodeSavingA,
    }
  );
  const loadingFileB = useLoadFile(
    {entityName, projectName, runName: runB!.name},
    codePathB,
    {
      onSuccess: setCodeBCallback,
      onFailure: setErrorCallback,
      fallback: setErrorCallback,
      skip: !isUsingCodeSavingB,
    }
  );

  const loadingArtifactFileA = useLoadFileInArtifact(
    {entityName, projectName, runName: runA!.name},
    codePathA,
    {
      onSuccess: setCodeACallback,
      onFailure: setErrorCallback,
      fallback: setErrorCallback,
      skip: isUsingCodeSavingA,
    }
  );

  const loadingArtifactFileB = useLoadFileInArtifact(
    {entityName, projectName, runName: runB!.name},
    codePathB,
    {
      onSuccess: setCodeBCallback,
      onFailure: setErrorCallback,
      fallback: setErrorCallback,
      skip: isUsingCodeSavingB,
    }
  );

  if (
    loadingFileA ||
    loadingFileB ||
    loadingArtifactFileA ||
    loadingArtifactFileB
  ) {
    return <Loader name="code-diff-loader" />;
  }

  const highlightSyntax = (str: string) => (
    <pre
      style={{display: 'inline'}}
      dangerouslySetInnerHTML={{
        __html: window.Prism.highlight(
          str || '',
          window.Prism.languages.python
        ),
      }}
    />
  );

  return (
    <ReactDiffViewer
      oldValue={codeA}
      newValue={codeB}
      splitView={split}
      showDiffOnly={codeA !== codeB}
      renderContent={highlightSyntax}
    />
  );
};

export default CodeDiff;
